'use client'

import { useEffect, useState } from 'react'
import { AddBankDataButton } from 'refunds/components/AddBankDataButton/AddBankDataButton'
import { SAVE_DATA_STATUS } from 'refunds/constants/constants'
import { BankInfoCard } from 'refunds/containers/BankInfoCard/BankInfoCard'
import type { RefundData } from 'refunds/types/refundData'

type FormContainer = React.ComponentType<{
	showTitle: boolean
	refundData: RefundData
	handleOnError: () => void
	handleOnSave: () => void
}>

type ModalComponent<T> = React.ComponentType<{
	refundData: RefundData
	handleOnError: () => void
	handleOnSave: () => void
	handleOnClose: () => void
	handleOnSubmit?: (values: T) => void
	validateWithoutSave: boolean
}>

interface UniversalBankContainerProps<T> {
	readonly onChangeFormVisibility: (showForm: boolean) => void
	readonly handleRequestStatus: (status: string) => void
	readonly handleOnSubmit?: (values: T) => void
	readonly reloadRefundData?: () => void
	readonly showNotification?: () => void
	readonly showDirectForm: boolean
	readonly title?: string
	readonly CardContent: React.ReactNode
	readonly FormContainer: FormContainer
	readonly ModalComponent: ModalComponent<T>
	readonly refundData: RefundData
}

export function UniversalBankContainer<T>({
	onChangeFormVisibility,
	handleRequestStatus,
	handleOnSubmit,
	reloadRefundData,
	showNotification,
	showDirectForm,
	title,
	CardContent,
	FormContainer,
	ModalComponent,
	refundData,
}: UniversalBankContainerProps<T>) {
	const [showForm, setShowForm] = useState(false)

	useEffect(() => {
		onChangeFormVisibility(showForm)
	}, [showForm])

	const handleEdit = () => {
		setShowForm(true)
	}

	const handleOnSave = () => {
		setShowForm(false)
		handleRequestStatus(SAVE_DATA_STATUS.OK)
		showNotification?.()
		reloadRefundData?.()
	}

	const handleOnError = () => {
		setShowForm(false)
		handleRequestStatus(SAVE_DATA_STATUS.KO)
	}

	const handleOnClose = () => {
		setShowForm(false)
	}

	const handleSubmit = (values: T) => {
		handleOnSubmit?.(values)
		handleOnClose()
	}

	if (showDirectForm) {
		return (
			<FormContainer
				showTitle
				refundData={refundData}
				handleOnError={handleOnError}
				handleOnSave={handleOnSave}
			/>
		)
	}

	return (
		<>
			{CardContent ? (
				<BankInfoCard
					title={title}
					handleEdit={handleEdit}
					showEditButton={true}
					bodyContent={CardContent}
				/>
			) : (
				<AddBankDataButton handleOpenBankDataModal={() => setShowForm(true)} />
			)}
			{showForm && (
				<ModalComponent
					refundData={refundData}
					handleOnError={handleOnError}
					handleOnSave={handleOnSave}
					handleOnClose={handleOnClose}
					handleOnSubmit={handleSubmit}
					validateWithoutSave={!!handleOnSubmit}
				/>
			)}
		</>
	)
}
